/**
 * Shared walls, linked openings, opening geometry, wall render parts and plan
 * checks (F6, F7, F8). Pure over a Plan.
 */
import {
  DOOR_ANGLE_RAD, MISMATCH_TOL_FT, POLICY, SHARED_TOL_FT,
} from './policy';
import { wallFtOf, wallIn } from './thickness';
import { EPS, along, area, axisOf, clamp, clone, dist, edgeOf, outN, unit } from './geom';
import { buildUF, edgeKnown, type UF } from './uf';
import type {
  Axis, Edge, Opening, Plan, Pt, Room, SharedInterval, SharedMap,
} from './types';

type Pair = { r: Room; o: Opening };

const facing = (nA: Pt, nB: Pt) => nA.x * nB.x + nA.y * nB.y <= POLICY.facing_dot_max;

/**
 * Shared walls: two rooms whose facing walls sit one wall thickness apart, the
 * thickness either room stores for that wall (FIELD-2 E6), within the policy's
 * shared-wall tolerance. The earlier room (lower index) is primary and draws it.
 */
export function sharedMap(plan: Plan): SharedMap {
  const M: SharedMap = new Map();
  const add = (rid: string, vid: number, iv: SharedInterval) => {
    if (!M.has(rid)) M.set(rid, new Map());
    const m = M.get(rid)!;
    if (!m.has(vid)) m.set(vid, []);
    m.get(vid)!.push(iv);
  };
  const rooms = plan.rooms;
  for (let i = 0; i < rooms.length; i++) {
    for (let j = i + 1; j < rooms.length; j++) {
      const A = rooms[i], B = rooms[j], aA = area(A), aB = area(B);
      A.corners.forEach((a1, ia) => {
        const a2 = A.corners[(ia + 1) % A.corners.length], t = axisOf(a1, a2);
        if (t !== 'h' && t !== 'v') return;
        const nA = outN(A, a1, a2, aA);
        const lx: Axis = t === 'h' ? 'y' : 'x', ox: Axis = t === 'h' ? 'x' : 'y';
        B.corners.forEach((b1, ib) => {
          const b2 = B.corners[(ib + 1) % B.corners.length];
          if (axisOf(b1, b2) !== t) return;
          const nB = outN(B, b1, b2, aB);
          if (!facing(nA, nB)) return;
          const g = (b1[lx] - a1[lx]) * nA[lx];
          if (Math.min(Math.abs(g - wallFtOf(A, a1.id)), Math.abs(g - wallFtOf(B, b1.id))) > SHARED_TOL_FT) return;
          const lo = Math.max(Math.min(a1[ox], a2[ox]), Math.min(b1[ox], b2[ox]));
          const hi = Math.min(Math.max(a1[ox], a2[ox]), Math.max(b1[ox], b2[ox]));
          if (hi - lo < POLICY.shared_min_overlap_ft) return;
          const offs = (p1: Pt, p2: Pt): [number, number] => {
            const u = unit(p1, p2);
            const s0 = (lo - p1[ox]) * u[ox], s1 = (hi - p1[ox]) * u[ox];
            return [Math.min(s0, s1), Math.max(s0, s1)];
          };
          const [sa0, sa1] = offs(a1, a2), [sb0, sb1] = offs(b1, b2);
          add(A.id, a1.id, { lo: sa0, hi: sa1, primary: true, orid: B.id, ovid: b1.id, gapFt: g });
          add(B.id, b1.id, { lo: sb0, hi: sb1, primary: false, orid: A.id, ovid: a1.id, gapFt: g });
        });
      });
    }
  }
  return M;
}

/** Coordinate-group roots on axis ax held by a shared wall (cannot move). */
export function heldRoots(plan: Plan, r: Room, U: UF, ax: Axis, M?: SharedMap): Set<number> {
  const m = (M ?? sharedMap(plan)).get(r.id);
  const set = new Set<number>();
  if (!m) return set;
  m.forEach((_iv, vid) => {
    const E = edgeOf(r, vid);
    if (!E) return;
    const t = axisOf(E.a, E.b);
    if ((t === 'h' ? 'y' : 'x') === ax) set.add(U.find(ax, vid));
  });
  return set;
}

/** A coordinate is pinned when its group holds a shared wall (F5, F6). */
export function isPinned(plan: Plan, r: Room, U: UF, ax: Axis, vid: number, M?: SharedMap): boolean {
  return heldRoots(plan, r, U, ax, M).has(U.find(ax, vid));
}

export function ivAt(M: SharedMap, rid: string, vid: number, s: number): SharedInterval | null {
  const l = M.get(rid)?.get(vid);
  return l ? l.find((iv) => s >= iv.lo - 1e-6 && s <= iv.hi + 1e-6) ?? null : null;
}

/** The opening with the same link in another room, adjacent or not. */
export function partnerOf(plan: Plan, r: Room, o: Opening): Pair | null {
  if (o.link == null) return null;
  for (const q of plan.rooms) {
    if (q === r) continue;
    const p = q.openings.find((x) => x.link === o.link);
    if (p) return { r: q, o: p };
  }
  return null;
}

/** The partner, only while the two rooms still share the wall it sits on (F7). */
export function adjacent(plan: Plan, r: Room, o: Opening, M?: SharedMap): Pair | null {
  const map = M ?? sharedMap(plan);
  const iv = ivAt(map, r.id, o.edgeStart, o.offFt + o.widthFt / 2);
  const P = partnerOf(plan, r, o);
  return P && iv && iv.orid === P.r.id && iv.ovid === P.o.edgeStart ? P : null;
}

/** The secondary room's copy of a linked opening is not drawn. */
export function openHidden(plan: Plan, r: Room, o: Opening, M: SharedMap): boolean {
  const iv = ivAt(M, r.id, o.edgeStart, o.offFt + o.widthFt / 2);
  return !!(iv && !iv.primary && adjacent(plan, r, o, M));
}

/** Copy an edited opening onto its adjacent partner, mirrored across the wall. */
export function syncLinked(plan: Plan, r: Room, o: Opening): void {
  const P = adjacent(plan, r, o);
  if (!P) return;
  const E = edgeOf(r, o.edgeStart), E2 = edgeOf(P.r, P.o.edgeStart);
  if (!E || !E2) return;
  const u = unit(E.a, E.b), u2 = unit(E2.a, E2.b);
  const pr = (q: Pt) => (q.x - E2.a.x) * u2.x + (q.y - E2.a.y) * u2.y;
  const t0 = pr(along(E.a, E.b, o.offFt)), t1 = pr(along(E.a, E.b, o.offFt + o.widthFt));
  const same = u.x * u2.x + u.y * u2.y > 0;
  const copy = clone(o);
  copy.id = P.o.id;
  copy.edgeStart = P.o.edgeStart;
  copy.link = P.o.link;
  copy.offFt = Math.min(t0, t1);
  if (copy.door && o.door) {
    copy.door.hingeAtEnd = same ? !!o.door.hingeAtEnd : !o.door.hingeAtEnd;
    copy.door.out = !o.door.out;
    // B-7357: in / out name a side relative to the room holding the copy, like `out`.
    if (o.door.bifoldSide) copy.door.bifoldSide = o.door.bifoldSide === 'in' ? 'out' : 'in'; // policy-literal-ok: the in/out vocabulary (data contract)
  }
  for (const k of Object.keys(P.o) as (keyof Opening)[]) delete P.o[k];
  Object.assign(P.o, copy);
}

export interface OpenGeom {
  E: Edge; u: Pt; nO: Pt; p0: Pt; p1: Pt; thick: number; w: number; L: number; s0: number; s1: number;
  hinge?: Pt; leaf?: Pt; aJ?: number; aL?: number; ccw?: boolean; side?: Pt;
}

/** Opening geometry; a door's leaf is drawn open at the policy angle (F8). */
export function openGeom(r: Room, o: Opening, M: SharedMap, A?: number): OpenGeom | null {
  const E = edgeOf(r, o.edgeStart);
  if (!E) return null;
  const L = dist(E.a, E.b), u = unit(E.a, E.b), nO = outN(r, E.a, E.b, A);
  const s0 = clamp(o.offFt, 0, L), s1 = clamp(o.offFt + o.widthFt, 0, L);
  if (s1 - s0 < POLICY.min_segment_ft) return null;
  const p0 = { x: E.a.x + u.x * s0, y: E.a.y + u.y * s0 }, p1 = { x: E.a.x + u.x * s1, y: E.a.y + u.y * s1 };
  const iv = ivAt(M, r.id, o.edgeStart, (s0 + s1) / 2);
  const thick = iv && iv.primary ? wallFtOf(r, o.edgeStart) : 0;
  const g: OpenGeom = { E, u, nO, p0, p1, thick, w: s1 - s0, L, s0, s1 };
  if (o.kind === 'door') {
    const out = !!o.door?.out, atEnd = !!o.door?.hingeAtEnd;
    const face = out ? thick : 0, side = out ? nO : { x: -nO.x, y: -nO.y };
    const h0 = atEnd ? p1 : p0, j0 = atEnd ? p0 : p1;
    const hinge = { x: h0.x + nO.x * face, y: h0.y + nO.y * face };
    const jm = { x: j0.x + nO.x * face, y: j0.y + nO.y * face };
    const d = unit(hinge, jm), ca = Math.cos(DOOR_ANGLE_RAD), sa = Math.sin(DOOR_ANGLE_RAD);
    const lv = { x: d.x * ca + side.x * sa, y: d.y * ca + side.y * sa };
    g.hinge = hinge;
    g.leaf = { x: hinge.x + lv.x * g.w, y: hinge.y + lv.y * g.w };
    g.aJ = Math.atan2(d.y, d.x);
    g.aL = Math.atan2(lv.y, lv.x);
    let df = g.aL - g.aJ;
    while (df > Math.PI) df -= 2 * Math.PI;
    while (df < -Math.PI) df += 2 * Math.PI;
    g.ccw = df < 0;
    g.side = side;
  }
  return g;
}

type Seg = [number, number];
export function segMinus(segs: Seg[], cuts: Seg[]): Seg[] {
  let out = segs;
  cuts.forEach(([c0, c1]) => {
    const n: Seg[] = [];
    out.forEach(([s0, s1]) => {
      if (c1 <= s0 || c0 >= s1) { n.push([s0, s1]); return; }
      if (c0 > s0) n.push([s0, c0]);
      if (c1 < s1) n.push([c1, s1]);
    });
    out = n;
  });
  return out.filter((q) => q[1] - q[0] > 1e-4);
}
export function segInter(segs: Seg[], cuts: Seg[]): Seg[] {
  const n: Seg[] = [];
  segs.forEach(([s0, s1]) => cuts.forEach(([c0, c1]) => {
    const a = Math.max(s0, c0), b = Math.min(s1, c1);
    if (b - a > 1e-4) n.push([a, b]);
  }));
  return n;
}

export interface WallPart {
  rid: string; vid: number; a: Pt; b: Pt; u: Pt; nO: Pt; L: number;
  /** Solid (measured) or rough (dashed, "~" label). */
  known: boolean;
  /** Plain wall line runs, as [from, to] offsets along the wall. */
  lines: Seg[];
  /** Shared-wall bands drawn once, `thickFt` thick outward (none for a 0 in split line). */
  bands: Seg[];
  /** FIELD-2: the wall's stored thickness, feet and inches (E1, E19). */
  thickFt: number;
  thickIn: number;
  /** FIELD-2 E19: thicker than the plan's interior default, so it draws a second line at its outer face. */
  outerLine: boolean;
  /** True when the whole wall is the hidden side of a shared wall (no label). */
  hiddenLabel: boolean;
  /** True when part of the wall is the primary side of a shared wall. */
  sharedPrimary: boolean;
}

/** What to draw for every wall (rendering is the screen's; this is the logic). */
export function wallParts(plan: Plan, M?: SharedMap): WallPart[] {
  const SHM = M ?? sharedMap(plan);
  const interior = plan.defaults.interiorWallIn ?? POLICY.interior_wall_in;
  const out: WallPart[] = [];
  for (const r of plan.rooms) {
    const U = buildUF(r), A = area(r), n = r.corners.length, rm = SHM.get(r.id);
    for (let i = 0; i < n; i++) {
      const a = r.corners[i], b = r.corners[(i + 1) % n], L = dist(a, b);
      if (L < 1e-6) continue;
      const ivs = rm?.get(a.id) ?? [];
      const cuts: Seg[] = r.openings
        .filter((o) => o.edgeStart === a.id && !openHidden(plan, r, o, SHM))
        .map((o) => [clamp(o.offFt, 0, L), clamp(o.offFt + o.widthFt, 0, L)]);
      const vis = segMinus(segMinus([[0, L]], cuts), ivs.filter((v) => !v.primary).map((v) => [v.lo, v.hi] as Seg));
      const pri = ivs.filter((v) => v.primary && v.gapFt > EPS).map((v) => [v.lo, v.hi] as Seg);
      const tIn = wallIn(r, a.id);
      out.push({
        rid: r.id, vid: a.id, a, b, u: unit(a, b), nO: outN(r, a, b, A), L,
        known: edgeKnown(U, a, b),
        lines: segMinus(vis, pri),
        bands: segInter(vis, pri),
        thickFt: wallFtOf(r, a.id),
        thickIn: tIn,
        outerLine: tIn > interior,
        hiddenLabel: ivs.some((x) => !x.primary && x.hi - x.lo > L - POLICY.label_hide_margin_ft),
        sharedPrimary: ivs.some((x) => x.primary),
      });
    }
  }
  return out;
}

export interface Mismatch {
  rid: string;
  /** Positive: gap beyond wall thickness; negative: overlap. Feet. */
  err: number;
  dx: number;
  dy: number;
  pos: Pt;
}

/** Facing walls that are near but not at wall thickness (red marks). */
export function mismatches(plan: Plan): Mismatch[] {
  const out: Mismatch[] = [];
  const rooms = plan.rooms;
  for (let i = 0; i < rooms.length; i++) {
    for (let j = i + 1; j < rooms.length; j++) {
      const A = rooms[i], B = rooms[j], aA = area(A), aB = area(B);
      const seen = new Set<string>();
      A.corners.forEach((a1, ia) => {
        const a2 = A.corners[(ia + 1) % A.corners.length], ta = axisOf(a1, a2);
        if (ta !== 'h' && ta !== 'v') return;
        const nA = outN(A, a1, a2, aA);
        B.corners.forEach((b1, ib) => {
          const b2 = B.corners[(ib + 1) % B.corners.length];
          if (axisOf(b1, b2) !== ta) return;
          const nB = outN(B, b1, b2, aB);
          if (!facing(nA, nB)) return;
          const lx: Axis = ta === 'h' ? 'y' : 'x', ox: Axis = ta === 'h' ? 'x' : 'y';
          const lo = Math.max(Math.min(a1[ox], a2[ox]), Math.min(b1[ox], b2[ox]));
          const hi = Math.min(Math.max(a1[ox], a2[ox]), Math.max(b1[ox], b2[ox]));
          if (hi - lo < POLICY.mismatch_min_overlap_ft) return;
          const g = (b1[lx] - a1[lx]) * nA[lx];
          if (g < POLICY.mismatch_gap_min_ft || g > POLICY.mismatch_gap_max_ft) return;
          if (Math.abs(g) < MISMATCH_TOL_FT) return; // split rooms meet at zero thickness
          // The stored thickness nearer the gap (E6): either room's entry for this wall.
          const tA = wallFtOf(A, a1.id), tB = wallFtOf(B, b1.id);
          const err = Math.abs(g - tA) <= Math.abs(g - tB) ? g - tA : g - tB;
          if (Math.abs(err) < MISMATCH_TOL_FT) return;
          const key = lx + Math.round(err / MISMATCH_TOL_FT);
          if (seen.has(key)) return;
          seen.add(key);
          const pos = { x: 0, y: 0 };
          pos[ox] = (lo + hi) / 2;
          pos[lx] = a1[lx] + (nA[lx] * g) / 2;
          out.push({ rid: B.id, err, dx: -err * nA.x, dy: -err * nA.y, pos });
        });
      });
    }
  }
  return out;
}

/** Tap a red mark: move the later room to close the gap or overlap. */
export function closeMismatch(plan: Plan, m: Mismatch): { ok: true; closed: 'gap' | 'overlap' } | { ok: false; err: 'no-room'; message: string } {
  const r = plan.rooms.find((q) => q.id === m.rid);
  if (!r) return { ok: false, err: 'no-room', message: 'That room is gone' };
  r.corners.forEach((p) => { p.x += m.dx; p.y += m.dy; });
  return { ok: true, closed: m.err > 0 ? 'gap' : 'overlap' };
}
